import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, InputAdornment, Link, TextField, Tooltip, Typography } from "@mui/material";
import { Check, Close, EditOutlined } from "@mui/icons-material";

interface InlineEditFieldProps {
    label: string;
    /** What's shown when not editing (null → "—", or the "Add …" link). */
    display: React.ReactNode;
    /** The value the field starts with. */
    raw: string;
    editable: boolean;
    type?: "text" | "number";
    prefix?: string;
    suffix?: string;
    /** Shown instead of "—" when there's no value yet, e.g. "Add barcode". */
    addLabel?: string;
    placeholder?: string;
    /** Return an error to show, or null when the value is fine. */
    validate?: (value: string) => string | null;
    /** Save the value; resolves to null when saved, else the error to show. */
    onSave: (value: string) => Promise<string | null>;
    onDirtyChange?: (dirty: boolean) => void;
    /** Extra buttons next to the value (e.g. copy). */
    action?: React.ReactNode;
    /** Shown under the field while typing (e.g. the new margin); can put a
     * suggestion into the field with `setDraft`. */
    hint?: (draft: string, setDraft: (value: string) => void) => React.ReactNode;
    /** Tidies text as it's typed (e.g. a web address: lowercase, hyphens). */
    transform?: (value: string) => string;
    /** "row" = label left, value right (details list); "stat" = small label
     * over a big value (prices); "title" = the drawer's heading. */
    layout?: "row" | "stat" | "title";
    statColor?: string;
}

/**
 * One value in the product drawer that can be changed in place: a pencil
 * next to it opens a small field; Enter (or ✓) saves just that value, Esc
 * (or ✕) puts it back.
 */
export const InlineEditField: React.FC<InlineEditFieldProps> = ({
    label,
    display,
    raw,
    editable,
    type = "text",
    prefix,
    suffix,
    addLabel,
    placeholder,
    validate,
    onSave,
    onDirtyChange,
    action,
    hint,
    transform,
    layout = "row",
    statColor,
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(raw);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const dirty = editing && draft.trim() !== raw.trim();
    useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

    // A different product (or a fresh value) closes the editor.
    useEffect(() => {
        setEditing(false);
        setDraft(raw);
        setError("");
    }, [raw]);

    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);

    const start = () => {
        setDraft(raw);
        setError("");
        setEditing(true);
    };
    const cancel = () => {
        setEditing(false);
        setDraft(raw);
        setError("");
    };
    const submit = async () => {
        if (saving) return;
        if (!dirty) {
            cancel();
            return;
        }
        const problem = validate?.(draft.trim()) ?? null;
        if (problem) {
            setError(problem);
            return;
        }
        setSaving(true);
        const failed = await onSave(draft.trim());
        setSaving(false);
        if (failed) setError(failed);
        else setEditing(false);
    };

    const empty = raw.trim() === "";
    // An empty value already shows its "Add …" link.
    const pencil = editable && !editing && !(empty && addLabel) && (
        <Tooltip title={`Edit ${label.toLowerCase()}`}>
            <IconButton size="small" onClick={start} aria-label={`Edit ${label.toLowerCase()}`} sx={{ p: { xs: 1.75, sm: 0.75 }, m: { xs: -1, sm: 0 }, my: { xs: -1.25, sm: -0.5 } }}>
                <EditOutlined sx={{ fontSize: 16 }} />
            </IconButton>
        </Tooltip>
    );

    const shown =
        empty && editable && addLabel ? (
            <Link component="button" type="button" variant="body2" onClick={start} underline="hover">
                {addLabel}
            </Link>
        ) : (
            display ?? <em style={{ opacity: 0.4 }}>—</em>
        );

    const field = (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TextField
                    inputRef={inputRef}
                    size="small"
                    fullWidth
                    type={type}
                    value={draft}
                    placeholder={placeholder}
                    onChange={(e) => {
                        setDraft(transform ? transform(e.target.value) : e.target.value);
                        if (error) setError("");
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            void submit();
                        } else if (e.key === "Escape") {
                            // Cancel the edit without closing the drawer.
                            e.stopPropagation();
                            cancel();
                        }
                    }}
                    error={!!error}
                    disabled={saving}
                    inputProps={{
                        "aria-label": label,
                        ...(type === "number" ? { min: 0, step: "any", inputMode: "decimal" } : {}),
                    }}
                    InputProps={{
                        startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : undefined,
                        endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined,
                    }}
                />
                {saving ? (
                    <CircularProgress size={20} sx={{ mx: 1 }} />
                ) : (
                    <>
                        <Tooltip title="Save (Enter)">
                            <IconButton color="primary" onClick={() => void submit()} aria-label={`Save ${label.toLowerCase()}`}>
                                <Check fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel (Esc)">
                            <IconButton onClick={cancel} aria-label="Cancel">
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Box>
            {error ? (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 0.25 }}>
                    {error}
                </Typography>
            ) : (
                hint?.(draft, (value) => {
                    setDraft(value);
                    setError("");
                })
            )}
        </Box>
    );

    if (layout === "title") {
        return editing ? (
            field
        ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
                <Typography
                    variant="h6"
                    component="h2"
                    sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
                    title={raw}
                >
                    {shown}
                </Typography>
                {pencil}
            </Box>
        );
    }

    if (layout === "stat") {
        return (
            <Box>
                <Typography variant="caption" color="text.secondary">
                    {label}
                </Typography>
                {editing ? (
                    <Box sx={{ mt: 0.5 }}>{field}</Box>
                ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        <Typography variant="h6" fontWeight="bold" color={statColor}>
                            {shown}
                        </Typography>
                        {pencil}
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: editing ? "flex-start" : "center",
                py: 0.75,
                gap: 2,
                minHeight: 36,
            }}
        >
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, flexShrink: 0, pt: editing ? 1 : 0 }}>
                {label}
            </Typography>
            {editing ? (
                field
            ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto", minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="medium" sx={{ textAlign: "right", wordBreak: "break-word" }}>
                        {shown}
                    </Typography>
                    {!empty && action}
                    {pencil}
                </Box>
            )}
        </Box>
    );
};

export default InlineEditField;
